import type { TableProps } from 'antd';
import type { ColumnType } from 'antd/es/table';
import type { Key, ReactNode } from 'react';

export type DataTableSort = {
  key: string;
  direction: 'ascend' | 'descend';
} | null;

export type DataTableLayout = {
  widths: Record<string, number>;
  hidden: string[];
  order: string[];
};

type DataTableColumnBase<T> = Pick<
  ColumnType<T>,
  'align' | 'fixed' | 'ellipsis'
> & {
  id: string;
  title: ReactNode;
  /** Omit for content-sized columns; pass a number only when a fixed size is required. */
  width?: number;
  render: (row: T) => ReactNode;
  minWidth?: number;
  headerAlign?: 'left' | 'right' | 'center';
  responsivePriority?: number;
  mobileRender?: (row: T) => ReactNode;
  isAvailable?: boolean;
  defaultHidden?: boolean;
  locked?: boolean;
};

type DataTableSortableColumn<T> =
  | {
      sortable?: true;
      sortKey: string;
      clientSorter?: (a: T, b: T) => number;
    }
  | {
      sortable?: true;
      sortKey?: string;
      clientSorter: (a: T, b: T) => number;
    };

type DataTableUnsortableColumn = {
  sortable: false;
  sortKey?: never;
  clientSorter?: never;
};

export type DataTableColumn<T> = DataTableColumnBase<T> &
  (DataTableSortableColumn<T> | DataTableUnsortableColumn);

export type DataTableSelection<T> = {
  selectedRowKeys: Key[];
  onChange: (selectedRowKeys: Key[], selectedRows: T[]) => void;
  getCheckboxProps?: NonNullable<
    TableProps<T>['rowSelection']
  >['getCheckboxProps'];
  preserveSelectedRowKeys?: boolean;
  /** When true, hide the table header select-all checkbox (row checkboxes remain). */
  hideSelectAll?: boolean;
};

export type DataTableProps<T> = {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  layout: DataTableLayout;
  onLayoutChange: (layout: DataTableLayout) => void;
  loading?: boolean;
  sort?: DataTableSort;
  onSortChange?: (sort: DataTableSort) => void;
  selection?: DataTableSelection<T>;
  expandable?: TableProps<T>['expandable'];
  pagination?: TableProps<T>['pagination'];
  scrollY?: number;
  /** Opt-in antd Table virtualization for long lists. */
  virtual?: boolean;
  onRowClick?: (row: T) => void;
  getRowTestId?: (row: T) => string;
  /** Replaces the table-body empty copy. Defaults to `NO_DATA`. */
  emptyDescription?: ReactNode;
  'data-testid'?: string;
};

export function createDefaultDataTableLayout<T>(
  columns: DataTableColumn<T>[],
): DataTableLayout {
  return {
    widths: {},
    hidden: columns
      .filter((column) => column.isAvailable !== false && column.defaultHidden)
      .map(({ id }) => id),
    order: columns
      .filter((column) => column.isAvailable !== false)
      .map(({ id }) => id),
  };
}
